/**
 * Valida un email usando una combinación de técnicas para mayor robustez
 * @param {string} email - El email a validar
 * @returns {boolean} - true si el email es válido, false en caso contrario
 */
export const validateEmail = (email) => {
  // Verificaciones básicas
  if (!email || typeof email !== 'string') {
    return false;
  }

  // Trim y verificar longitud
  const trimmedEmail = email.trim();
  if (trimmedEmail.length === 0 || trimmedEmail.length > 254) {
    return false;
  }

  // Verificar estructura básica (debe tener exactamente un @)
  const atCount = (trimmedEmail.match(/@/g) || []).length;
  if (atCount !== 1) {
    return false;
  }

  const [localPart, domainPart] = trimmedEmail.split('@');

  // Validar parte local (antes del @)
  if (!isValidLocalPart(localPart)) {
    return false;
  }

  // Validar parte del dominio (después del @)
  if (!isValidDomainPart(domainPart)) {
    return false;
  }

  return true;
};

/**
 * Valida la parte local del email (antes del @)
 * @param {string} localPart - La parte local del email
 * @returns {boolean} - true si es válida
 */
const isValidLocalPart = (localPart) => {
  if (!localPart || localPart.length === 0 || localPart.length > 64) {
    return false;
  }

  // No puede empezar o terminar con punto
  if (localPart.startsWith('.') || localPart.endsWith('.')) {
    return false;
  }

  // No puede tener puntos consecutivos
  if (localPart.includes('..')) {
    return false;
  }

  // Caracteres permitidos en la parte local
  const localPartRegex = /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*$/;

  return localPartRegex.test(localPart);
};

/**
 * Valida la parte del dominio del email (después del @)
 * @param {string} domainPart - La parte del dominio del email
 * @returns {boolean} - true si es válida
 */
const isValidDomainPart = (domainPart) => {
  if (!domainPart || domainPart.length === 0 || domainPart.length > 253) {
    return false;
  }

  // No puede empezar o terminar con guión o punto
  if (domainPart.startsWith('-') || domainPart.endsWith('-') ||
    domainPart.startsWith('.') || domainPart.endsWith('.')) {
    return false;
  }

  // Dividir en labels (partes separadas por puntos)
  const labels = domainPart.split('.');

  // Debe tener al menos 2 labels (ej: example.com)
  if (labels.length < 2) {
    return false;
  }

  // Validar cada label
  for (const label of labels) {
    if (!isValidDomainLabel(label)) {
      return false;
    }
  }

  // Verificar si es una dirección IP (todos los labels son números)
  const isIPAddress = labels.every(label => /^\d+$/.test(label) && parseInt(label) <= 255);
  
  if (!isIPAddress) {
    // Si no es IP, el último label (TLD) debe tener al menos 2 caracteres y solo letras
    const tld = labels[labels.length - 1];
    if (tld.length < 2 || !/^[a-zA-Z]+$/.test(tld)) {
      return false;
    }
  }

  return true;
};

/**
 * Valida un label individual del dominio
 * @param {string} label - Un label del dominio
 * @returns {boolean} - true si es válido
 */
const isValidDomainLabel = (label) => {
  if (!label || label.length === 0 || label.length > 63) {
    return false;
  }

  // No puede empezar o terminar con guión
  if (label.startsWith('-') || label.endsWith('-')) {
    return false;
  }

  // Solo puede contener letras, números y guiones
  const labelRegex = /^[a-zA-Z0-9-]+$/;
  return labelRegex.test(label);
};

// Función adicional para validación más estricta (opcional)
/**
 * Validación más estricta que también verifica algunos casos edge
 * @param {string} email - El email a validar
 * @returns {boolean} - true si el email es válido
 */
export const validateEmailStrict = (email) => {
  if (!validateEmail(email)) {
    return false;
  }

  const trimmedEmail = email.trim();

  // Verificaciones adicionales más estrictas

  // No debe contener espacios
  if (trimmedEmail.includes(' ')) {
    return false;
  }

  // No debe tener caracteres de control
  // eslint-disable-next-line no-control-regex
  if (/[\x00-\x1f\x7f]/.test(trimmedEmail)) {
    return false;
  }

  // Verificar que no sea solo números en el dominio
  const [, domainPart] = trimmedEmail.split('@');
  const labels = domainPart.split('.');

  // Al menos un label que no sea solo números
  const hasNonNumericLabel = labels.some(label => !/^\d+$/.test(label));
  if (!hasNonNumericLabel) {
    return false;
  }

  return true;
};